import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Plus, Droplet, Infinity as InfinityIcon, Fingerprint } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useProducts } from "../hooks/useShop";
import { useCart } from "../context/CartContext";
import {
  ambassadors,
  testimonials,
  journal,
  dualImage,
  logoImage,
  HANDLES,
  BRAND,
} from "../mock/mock";

const principles = [
  {
    icon: Droplet,
    kicker: "Hohe Duftölkonzentration",
    title: "35 % Duftöl",
    text: "Eine außergewöhnlich hohe Konzentration bildet die Grundlage — für intensive, ausdrucksstarke Dufterlebnisse mit Tiefe und Charakter.",
  },
  {
    icon: InfinityIcon,
    kicker: "Jenseits von Konventionen",
    title: "Unisex. Ohne Grenzen.",
    text: "Keine klassische Einteilung in Düfte für sie oder ihn. Unsere Kompositionen sind für Menschen geschaffen, die nach Charakter wählen.",
  },
  {
    icon: Fingerprint,
    kicker: "Unverwechselbarer Charakter",
    title: "Geschaffen mit Signatur.",
    text: "Jeder Roi-des-Rois-Duft besitzt seinen eigenen Charakter — von kraftvoller Tiefe bis klarer Frische. Deine Nummer. Deine Identität.",
  },
];

const ProductFeature = ({ product, reverse }) => {
  const { addItem } = useCart();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center reveal">
      {/* Image */}
      <div className={`${reverse ? "lg:order-2" : ""}`}>
        <Link to={`/product/${product.slug}`} className="block">
          <div className="aspect-[4/5] bg-white border border-[#e7e0d3] overflow-hidden">
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            />
          </div>
        </Link>
      </div>

      {/* Details */}
      <div className={`${reverse ? "lg:order-1" : ""}`}>
        <p className="text-[11px] tracking-[0.35em] uppercase text-gold mb-3">
          Extrait de Parfum · {product.size}
        </p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif text-4xl md:text-5xl text-[#17140f] leading-none hover:text-gold transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={13} className="fill-gold text-gold" />
            ))}
          </div>
          <span className="text-[#8d8779] text-xs">({product.reviews})</span>
        </div>

        <p className="text-[#5b564a] leading-relaxed mt-5 max-w-md">
          {product.description}
        </p>

        <div className="flex items-center gap-5 mt-7">
          <span className="text-2xl font-serif text-[#17140f]">
            €{product.price.toFixed(2)}
          </span>
          <button
            onClick={() => addItem(product)}
            className="group bg-gold text-[#14110c] text-[11px] tracking-[0.2em] uppercase px-7 py-3.5 font-medium hover:bg-[#b5892f] hover:text-white transition-colors flex items-center gap-2"
          >
            <Plus size={15} /> In den Warenkorb
          </button>
        </div>

        {/* Notes panel */}
        <div className="mt-9 border border-[#e7e0d3] bg-white">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#17140f] px-6 pt-5 pb-3">
            Duftnoten & Komposition
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#e7e0d3] border-t border-[#e7e0d3]">
            <NoteCol label="Kopfnote" notes={product.top} />
            <NoteCol label="Herznote" notes={product.heart} />
            <NoteCol label="Basisnote" notes={product.base} />
          </div>
        </div>
      </div>
    </div>
  );
};

const NoteCol = ({ label, notes }) => (
  <div className="px-6 py-5">
    <p className="text-gold text-[10px] tracking-[0.25em] uppercase mb-2">
      {label}
    </p>
    <p className="text-[#5b564a] text-sm leading-relaxed">{notes.join(", ")}</p>
  </div>
);

const Home = () => {
  useScrollReveal();
  const { products, loading } = useProducts();

  return (
    <div>
      {/* HERO */}
      <section className="bg-[#fbf8f2] pt-14 md:pt-20 pb-16 text-center">
        <div className="max-w-3xl mx-auto px-5 animate-fade-up">
          <img
            src={logoImage}
            alt="Roi des Rois"
            className="h-24 w-24 object-cover rounded-sm border border-[#e7e0d3] mx-auto mb-8"
          />
          <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-5 leading-relaxed">
            Royalty by Roi des Rois | 2025
            <span className="block mt-1">1 of 750</span>
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-[#17140f] leading-[1.05]">
            Der Duft der Könige
          </h1>
          <p className="text-[#5b564a] text-lg mt-6 leading-relaxed max-w-xl mx-auto">
            Düfte, geschaffen für eine unverwechselbare Präsenz. {BRAND.name}{" "}
            verbindet zeitlose Eleganz mit außergewöhnlicher Intensität und
            charakterstarker Komposition.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link
              to="/shop"
              className="group bg-gold text-[#14110c] text-[12px] tracking-[0.2em] uppercase px-9 py-4 font-medium hover:bg-[#b5892f] hover:text-white transition-colors flex items-center gap-2"
            >
              Jetzt einkaufen
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={`/product/${HANDLES.rdr}`}
              className="border border-[#17140f] text-[#17140f] text-[12px] tracking-[0.2em] uppercase px-9 py-4 hover:bg-[#17140f] hover:text-white transition-colors"
            >
              Die Kollektion
            </Link>
          </div>
          <div className="gold-line h-px w-24 mx-auto mt-14" />
        </div>
      </section>

      {/* PRODUCT FEATURES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-4 pb-8 space-y-24">
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="aspect-[4/5] bg-[#ece4d6] animate-pulse" />
            <div className="space-y-4">
              <div className="h-10 w-40 bg-[#ece4d6] animate-pulse" />
              <div className="h-24 w-full bg-[#ece4d6] animate-pulse" />
            </div>
          </div>
        ) : (
          products.map((p, i) => (
            <ProductFeature key={p.id} product={p} reverse={i % 2 === 1} />
          ))
        )}
      </section>

      {/* PRINCIPLES */}
      <section className="bg-white border-y border-[#e7e0d3] mt-20 py-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-16 reveal">
            <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-3">
              Unsere Prinzipien
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#17140f]">
              Was einen Duft von Roi des Rois ausmacht
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 reveal">
            {principles.map((pr, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full border border-gold flex items-center justify-center mx-auto mb-5">
                  <pr.icon size={22} className="text-gold" />
                </div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#8d8779] mb-2">
                  {pr.kicker}
                </p>
                <h3 className="font-serif text-2xl text-[#17140f] mb-3">
                  {pr.title}
                </h3>
                <p className="text-[#5b564a] text-sm leading-relaxed max-w-xs mx-auto">
                  {pr.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL SHOWCASE */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="reveal">
          <img src={dualImage} alt="1.RDR & 2.ROI VUE" className="w-full rounded-sm border border-[#e7e0d3]" />
        </div>
        <div className="reveal">
          <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-4">
            Extrait de Parfum · 35 %
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#17140f] leading-tight">
            Zwei Kronen. <br /> Ein Standard.
          </h2>
          <p className="text-[#5b564a] mt-6 leading-relaxed max-w-md">
            Jeder Flakon ist nummeriert und in limitierter Auflage von nur 750
            Stück gefertigt. Rohstoffe auf dem Niveau großer Couturierhäuser.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 mt-8 text-gold text-[11px] tracking-[0.2em] uppercase hover-underline"
          >
            Beide Editionen entdecken <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* AMBASSADORS */}
      <section className="bg-white border-y border-[#e7e0d3] py-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-14 reveal">
            <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-3">Werbepartner</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#17140f]">Getragen von Ikonen</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 reveal">
            {ambassadors.map((a) => (
              <Link key={a.id} to={`/product/${a.handle}`} className="group relative overflow-hidden block bg-white border border-[#e7e0d3]">
                <img src={a.image} alt={a.name} className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <img src={logoImage} alt="Roi des Rois" className="absolute top-4 right-4 h-12 w-12 md:h-14 md:w-14 object-cover rounded-sm border border-gold/40 shadow-lg" />
                <div className="p-6 border-t border-[#e7e0d3]">
                  <p className="text-[#17140f] text-sm tracking-[0.15em] uppercase">{a.name}</p>
                  <p className="text-gold text-xs mt-1">{a.role}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="text-center mb-14 reveal">
          <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-3">Stimmen</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#17140f]">Was man über uns sagt</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 reveal">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-white border border-[#e7e0d3] p-8 flex flex-col">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} size={14} className="fill-gold text-gold" />
                ))}
              </div>
              <p className="text-[#3f3b32] leading-relaxed italic font-serif text-lg flex-1">
                “{t.text}”
              </p>
              <div className="mt-6 pt-4 border-t border-[#e7e0d3]">
                <p className="text-[#17140f] text-sm">{t.name}</p>
                <p className="text-gold text-xs">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#f7f1e8] border-y border-[#e7e0d3] py-24">
        <div className="max-w-xl mx-auto px-5 text-center reveal">
          <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-3">Newsletter</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#17140f] leading-tight">
            Werde Teil der Welt von Roi des Rois
          </h2>
          <p className="text-[#5b564a] mt-5 mb-8">
            Erhalte exklusiven Zugang zu neuen Editionen und Neuigkeiten aus der
            Welt von Roi des Rois.
          </p>
          <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              required
              placeholder="Deine E-Mail-Adresse"
              className="flex-1 bg-white border border-[#dcd4c5] px-4 py-3.5 text-sm text-[#17140f] placeholder:text-[#8d8779] focus:outline-none focus:border-gold"
            />
            <button type="submit" className="bg-gold text-[#14110c] text-[11px] tracking-[0.2em] uppercase px-7 py-3.5 font-medium hover:bg-[#b5892f] hover:text-white transition-colors">
              Sichern
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
